import { useEffect, useMemo } from 'react';
import {
    WaypointTypeEnum,
    TmsRouteTypeEnum
} from '../../../../enums/TmsEnum';
import { useFilteredCarriers } from '../../hooks/useFilteredCarriers';
import { SiteType } from '../../../../graphql/types/SiteType';
import { TmsCarrierType } from '../graphql/queries/getTmsCarriers';
import { DrawerMode, type StationRow, type WarehouseOption, type RouteFormFilters } from '../types/routeFormTypes';

interface UseRouteFormOptionsParams {
    filters: RouteFormFilters;
    carriersData: TmsCarrierType[] | undefined;
    warehouseOptions: WarehouseOption[];
    externalStationNameList: string[];
    hubId: string | null;
    /** 当前 Hub 是否测试仓 */
    currentHubIsTest: boolean;
    isExternalStationMode: boolean;
    mode: DrawerMode;
    setTableData: React.Dispatch<React.SetStateAction<StationRow[]>>;
}

export const useRouteFormOptions = ({
    filters,
    carriersData,
    warehouseOptions,
    externalStationNameList,
    hubId,
    currentHubIsTest,
    isExternalStationMode,
    mode,
    setTableData,
}: UseRouteFormOptionsParams) => {

    // 承运商选项：按路线类型过滤（carrierSourceType 已在交接方需求中废弃，不再按内部/三方过滤）
    const carrierOptions = useFilteredCarriers(
        carriersData,
        filters.routeType
    );

    // 测试仓只能选测试站点，正式仓只能选正式站点；作为下面所有下拉选项的基础范围
    const testScopedWarehouseOptions = useMemo(
        () => warehouseOptions.filter(w => w.isTest === currentHubIsTest),
        [warehouseOptions, currentHubIsTest]
    );

    // 根据路线类型过滤途经点的仓库选项
    // 当routeType是Linehaul (1)时，途径点只能选择HUB
    // 当routeType是Feeder (2)时，途径点只能选择WHS (warehouse)
    const filteredWarehouseOptions = useMemo(() => {
        if (filters.routeType === TmsRouteTypeEnum.Linehaul) {
            // 干线路线：途径点只能选择HUB
            return testScopedWarehouseOptions.filter(w => w.siteType === SiteType.HUB);
        }
        if (filters.routeType === TmsRouteTypeEnum.Feeder && hubId) {
            // 支线路线：途径点只能选择WHS (warehouse)，且必须是当前hub下的warehouse
            return testScopedWarehouseOptions.filter(w =>
                w.siteType === SiteType.WAREHOUSE && w.parentSiteId === hubId
            );
        }
        // 未定义：显示所有warehouse
        return testScopedWarehouseOptions;
    }, [testScopedWarehouseOptions, filters.routeType, hubId]);

    // 根据routeType过滤终点的warehouse选项
    // 当routeType是Linehaul (1)时，终点只能选择HUB
    // 当routeType是Feeder (2)时，终点可选择WHS(warehouse)或当前hub
    const filteredDestinationOptions = useMemo(() => {
        if (filters.routeType === TmsRouteTypeEnum.Linehaul) {
            // 干线路线：终点只能选择HUB
            return testScopedWarehouseOptions.filter(w => w.siteType === SiteType.HUB);
        }
        if (filters.routeType === TmsRouteTypeEnum.Feeder && hubId) {
            // 支线路线：终点可选择WHS(warehouse)或当前hub
            // WHS：当前 hub 下的 warehouse（parentSiteId=hubId 且 siteType=WAREHOUSE）
            // 当前hub: siteType=HUB 且 id=hubId
            return testScopedWarehouseOptions.filter(w =>
                (w.siteType === SiteType.WAREHOUSE && w.parentSiteId === hubId) || // 当前hub下的warehouse
                (w.siteType === SiteType.HUB && w.id === hubId) // 当前hub
            );
        }
        // 未定义：使用与途经点相同的过滤选项
        return filteredWarehouseOptions;
    }, [filteredWarehouseOptions, filters.routeType, testScopedWarehouseOptions, hubId]);

    // 当routeType变化时，清理不在过滤列表中的站点
    useEffect(() => {
        if (isExternalStationMode) {
            return;
        }
        if (!filters.routeType) {
            return;
        }
        // 站点数据未加载完成时跳过，避免误清 edit 模式下已设置的站点值
        if (!warehouseOptions.length) {
            return;
        }
        // 支线需要 hubId 才能过滤
        if (filters.routeType === TmsRouteTypeEnum.Feeder && !hubId) {
            return;
        }
        const validWaypointSortingCodes = new Set(filteredWarehouseOptions.map(w => w.sortingCode));
        const validDestinationSortingCodes = new Set(filteredDestinationOptions.map(w => w.sortingCode));
        setTableData(prevData => {
            const hasInvalidStation = prevData.some(row => {
                if (row.type === WaypointTypeEnum.Origin) return false;
                if (row.type === WaypointTypeEnum.Waypoint) {
                    return row.station && !validWaypointSortingCodes.has(row.station);
                }
                if (row.type === WaypointTypeEnum.Destination) {
                    return row.station && !validDestinationSortingCodes.has(row.station);
                }
                return false;
            });

            if (!hasInvalidStation) return prevData;

            return prevData.map(row => {
                if (row.type === WaypointTypeEnum.Origin) return row;
                if (row.type === WaypointTypeEnum.Waypoint && row.station && !validWaypointSortingCodes.has(row.station)) {
                    return { ...row, station: '' };
                }
                if (row.type === WaypointTypeEnum.Destination && row.station && !validDestinationSortingCodes.has(row.station)) {
                    return { ...row, station: '' };
                }
                return row;
            });
        });
    }, [
        isExternalStationMode,
        filters.routeType,
        filters.businessType,
        hubId,
        filteredWarehouseOptions,
        filteredDestinationOptions,
    ]);

    // Temu：途经点/终点以配置 siteName 为准，配置或业务变化时清理无效选项
    useEffect(() => {
        if (!isExternalStationMode) {
            return;
        }
        // 编辑模式：不要自动清空（否则在配置列表/异步加载时会把已有值误清掉，导致"有时不显示"）
        if (mode === DrawerMode.Edit) {
            return;
        }
        // 配置尚未加载出来时，不做清理，避免把已有值清空
        if (!externalStationNameList || externalStationNameList.length === 0) {
            return;
        }
        const valid = new Set(externalStationNameList);
        setTableData(prevData => {
            const hasInvalid = prevData.some(row => {
                if (row.type === WaypointTypeEnum.Origin) {
                    return false;
                }
                if (!row.station) {
                    return false;
                }
                return !valid.has(row.station);
            });
            if (!hasInvalid) {
                return prevData;
            }
            return prevData.map(row => {
                if (row.type === WaypointTypeEnum.Origin) {
                    return row;
                }
                if (row.station && !valid.has(row.station)) {
                    return { ...row, station: '' };
                }
                return row;
            });
        });
    }, [isExternalStationMode, externalStationNameList, mode]);

    return {
        carrierOptions,
        filteredWarehouseOptions,
        filteredDestinationOptions,
    };
};
